/**
 * Blog utilities and types
 * Centralized blog management with type safety
 */

import { Locale } from './i18n';

export type BlogTag = 
  | 'Digital Innovation'
  | 'QR Ordering'
  | 'Kiosk Technology'
  | 'Tablet Ordering'
  | 'Restaurant Tips'
  | 'Industry Trends'
  | 'Customer Success'
  | 'Hotel Technology'
  | 'Upselling Strategies'
  | 'Self-Service Solutions';

export interface BlogMetadata {
  slug: string;
  publishedDate: string; // YYYY-MM-DD format
  updatedDate?: string; // Optional, for modified articles
  image: string; // Card/thumbnail image path
  heroImage?: string; // Optional separate hero image for blog post page
  tags: BlogTag[];
  featured?: boolean;
}

export interface BlogContent {
  title: string;
  excerpt: string;
  meta?: {
    title: string;
    description: string;
  };
  sections: BlogSection[];
}

export interface BlogSection {
  heading?: string; // Optional heading (h2)
  content: string; // HTML or text content
  image?: string; // Optional section image
}

export interface BlogPost {
  metadata: BlogMetadata;
  content: BlogContent;
  readTime: number; // Calculated in minutes
}

/**
 * Calculate reading time based on word count
 * Average reading speed: 200 words per minute
 */
export function calculateReadTime(content: BlogContent): number {
  const wordsPerMinute = 200;
  const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '');
  
  let totalText = content.excerpt;
  content.sections.forEach(section => {
    if (section.heading) totalText += ' ' + section.heading;
    totalText += ' ' + stripHtml(section.content);
  });
  
  const words = totalText.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(words / wordsPerMinute));
}

/**
 * Get all blog posts for a specific locale
 * Manually maintained list of blog post slugs for Next.js compatibility
 */
export async function getAllBlogPosts(locale: Locale): Promise<BlogPost[]> {
  // Manually define blog post slugs
  // Add new blog posts here as they are created
  const blogSlugs = [
    'bowling-alley-digital-ordering',
    'all-you-can-eat-ordering-system',
    'snackbar-digital-ordering',
    'restaurant-kpi-guide',
    'hotel-digital-ordering',
    'choosing-ordering-solution',
    'bakery-ordering-software',
    'qr-ordering-restaurant-guide',
    'golf-clubs-digital-ordering',
    'self-order-kiosks-for-hospitality',
    'the-psychology-behind-upselling',
    'the-new-hospitality-industry',
    'choosing-self-ordering-kiosk-software',
    'qr-ordering-implementation-guide',
    'all-you-can-eat-management-guide',
    'commission-free-online-ordering',
  ];
  
  const posts: BlogPost[] = [];
  
  for (const slug of blogSlugs) {
    try {
      const post = await getBlogPost(slug, locale);
      if (post) posts.push(post);
    } catch (error) {
      console.warn(`Failed to load blog post: ${slug}`, error);
    }
  }
  
  // Sort by date (newest first)
  return posts.sort((a, b) => 
    new Date(b.metadata.publishedDate).getTime() - 
    new Date(a.metadata.publishedDate).getTime()
  );
}

/**
 * Get a single blog post by slug
 */
export async function getBlogPost(slug: string, locale: Locale): Promise<BlogPost | null> {
  try {
    const metadata = await import(`@/content/blog/${slug}/metadata`);
    const content = await import(`@/content/blog/${slug}/content.${locale}`);
    
    const blogContent: BlogContent = content.default;
    const readTime = calculateReadTime(blogContent);
    
    return {
      metadata: metadata.default,
      content: blogContent,
      readTime,
    };
  } catch (error) {
    console.error(`Blog post not found: ${slug} (${locale})`, error);
    return null;
  }
}

/**
 * Get blog posts by tag
 */
export async function getBlogPostsByTag(tag: BlogTag, locale: Locale): Promise<BlogPost[]> {
  const allPosts = await getAllBlogPosts(locale);
  return allPosts.filter(post => post.metadata.tags.includes(tag));
}

/**
 * Get related blog posts based on shared tags
 */
export async function getRelatedPosts(
  currentSlug: string,
  tags: BlogTag[],
  locale: Locale,
  limit: number = 3
): Promise<BlogPost[]> {
  const allPosts = await getAllBlogPosts(locale);
  
  // Filter out current post and calculate relevance score
  const scoredPosts = allPosts
    .filter(post => post.metadata.slug !== currentSlug)
    .map(post => {
      const sharedTags = post.metadata.tags.filter(tag => tags.includes(tag));
      return { post, score: sharedTags.length };
    })
    .filter(item => item.score > 0) // Only posts with at least 1 shared tag
    .sort((a, b) => b.score - a.score); // Sort by relevance
  
  return scoredPosts.slice(0, limit).map(item => item.post);
}

/**
 * Get previous and next blog posts by publication date
 */
export async function getAdjacentPosts(
  currentSlug: string,
  locale: Locale
): Promise<{ prev: BlogPost | null; next: BlogPost | null }> {
  const allPosts = await getAllBlogPosts(locale);
  const currentIndex = allPosts.findIndex(post => post.metadata.slug === currentSlug);
  
  if (currentIndex === -1) return { prev: null, next: null };
  
  return {
    prev: currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null,
    next: currentIndex > 0 ? allPosts[currentIndex - 1] : null,
  };
}

/**
 * Get all unique tags from all blog posts
 */
export async function getAllTags(locale: Locale): Promise<BlogTag[]> {
  const allPosts = await getAllBlogPosts(locale);
  const tagsSet = new Set<BlogTag>();
  
  allPosts.forEach(post => {
    post.metadata.tags.forEach(tag => tagsSet.add(tag));
  });
  
  return Array.from(tagsSet).sort();
}

/**
 * Format date for display
 */
export function formatBlogDate(dateString: string, locale: Locale): string {
  const date = new Date(dateString);
  
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  
  const localeMap: Record<Locale, string> = {
    en: 'en-US',
    nl: 'nl-NL',
    de: 'de-DE',
    sv: 'sv-SE',
  };

  return date.toLocaleDateString(localeMap[locale], options);
}

